import { useState, useEffect, useCallback, useRef } from "react";
import { API_HOST } from "../constants";
import type { TelegramUser } from "../types";

const ONBOARDING_PAGE_KEYS = ["tasklist", "simulator", "sandbox", "lessonpage", "datadocs"] as const;

function getCompletedFromLocalStorage(): string[] {
  return ONBOARDING_PAGE_KEYS.filter(
    (key) => localStorage.getItem(`onboarding-${key}-completed`) === "true"
  );
}

/**
 * Возвращает список ключей страниц, для которых онбординг завершён.
 * Для авторизованного пользователя — из API, для анонима — из localStorage.
 */
export function useOnboardingCompleted(user: TelegramUser | null): {
  completedPages: string[];
  isLoading: boolean;
  refetch: () => void;
} {
  const [completedPages, setCompletedPages] = useState<string[]>(() =>
    user ? [] : getCompletedFromLocalStorage()
  );
  const [isLoading, setIsLoading] = useState(Boolean(user));
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    if (!user) {
      setCompletedPages(getCompletedFromLocalStorage());
      return;
    }
    setIsLoading(true);
    fetch(`${API_HOST}/user/${user.id}/onboarding/status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (mountedRef.current) {
          setCompletedPages(Array.isArray(data?.completed_pages) ? data.completed_pages : []);
        }
      })
      .catch(() => {
        if (mountedRef.current) setCompletedPages([]);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  }, [user?.id]);

  useEffect(() => {
    mountedRef.current = true;
    if (!user) {
      setCompletedPages(getCompletedFromLocalStorage());
      setIsLoading(false);
      return;
    }
    refetch();
    return () => {
      mountedRef.current = false;
    };
  }, [user?.id, refetch]);

  return { completedPages, isLoading, refetch };
}
